import { getGroups } from "../services/groupService";
import {
  getMembers,
  getMembersShort,
  getMembersShortNew,
} from "../services/memberService";
import { getBadgeById, getMembersWhoCanIssueABadge } from "../services/badgeService";
import { cachedRequest, mapWithConcurrency } from "./requestCache";
import { isAuthHttpError } from "./modalRequest";

export interface ModalMemberOption {
  _id?: string;
  id?: string;
  memberId?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  isActive?: boolean;
}

export interface ModalBadgeOption {
  _id: string;
  name: string;
  dueTimeRequired?: boolean;
  type?: string;
  maxMultiInvOff?: number;
}

export interface ModalGroupOption {
  _id: string;
  name?: string;
  groupId?: string;
  membersGroup?: Array<{ phone: string; [key: string]: any }>;
  nmGroup?: string[];
}

function normalizeLabel(s: string): string {
  return s.trim().replace(/\s+/g, " ");
}

export function normalizeMemberId(m: {
  _id?: string;
  id?: string;
  memberId?: string;
}): string | undefined {
  const id = m._id ?? m.id ?? m.memberId;
  return id != null ? String(id) : undefined;
}

function normalizeDirectoryMember(m: any): ModalMemberOption {
  const id = m?._id ?? m?.id ?? m?.memberId;
  return { ...m, _id: id != null ? String(id) : m?._id };
}

function extractMemberArray(response: any): any[] {
  const raw =
    response?.data?.results?.data ||
    response?.data?.data ||
    response?.data?.results ||
    [];
  return Array.isArray(raw) ? raw : [];
}

function filterDirectoryMembers(
  members: ModalMemberOption[],
  userId: string
): ModalMemberOption[] {
  return members
    .map(normalizeDirectoryMember)
    .filter(
      (m) =>
        normalizeMemberId(m) &&
        normalizeMemberId(m) !== userId &&
        m.isActive !== false &&
        (m.phone || m.firstName || m.name || m.lastName)
    );
}

export async function fetchModalMemberDirectory(
  userId: string
): Promise<ModalMemberOption[]> {
  return cachedRequest(`modal:members:${userId}`, async () => {
    try {
      const response = await getMembersShortNew({ page: 1, limit: 1000 });
      return filterDirectoryMembers(extractMemberArray(response), userId);
    } catch (primaryErr) {
      if (isAuthHttpError(primaryErr)) throw primaryErr;
      try {
        const response = await getMembersShort({ page: 1, limit: 1000 });
        return filterDirectoryMembers(extractMemberArray(response), userId);
      } catch (fallbackErr) {
        if (isAuthHttpError(fallbackErr)) throw fallbackErr;
        throw fallbackErr;
      }
    }
  });
}

export async function fetchModalGroups(): Promise<ModalGroupOption[]> {
  return cachedRequest("modal:groups", async () => {
    try {
      const response = await getGroups({ page: 1 });
      const groups =
        response?.data?.results?.data ||
        response?.data?.data ||
        response?.data?.groups ||
        [];
      return Array.isArray(groups)
        ? groups.filter((g: any) => g._id || g.groupId)
        : [];
    } catch (err) {
      if (isAuthHttpError(err)) throw err;
      throw err;
    }
  });
}

/** Warm directory + groups in the background (e.g. after login). */
export function prefetchCommitmentModalData(userId: string): void {
  if (!userId) return;
  void fetchModalMemberDirectory(userId);
  void fetchModalGroups();
  void fetchSenderActiveBadges(userId);
}

function mapToBadgeOptions(badges: any[]): ModalBadgeOption[] {
  return badges
    .filter((b: any) => b._id && b.name)
    .map((b: any) => ({
      _id: b._id,
      name: b.name,
      dueTimeRequired: b.dueTimeRequired,
      type: b.type,
      maxMultiInvOff: b.maxMultiInvOff,
    }));
}

export async function fetchSenderActiveBadges(
  userId: string
): Promise<ModalBadgeOption[]> {
  return cachedRequest(`modal:sender-badges:${userId}`, async () => {
    try {
      const response = await getMembers({ _id: userId });
      const memberData = response?.data?.results?.data?.[0];
      const badges = memberData?.badges || [];
      return mapToBadgeOptions(
        badges.filter((b: any) => b?.isActive === true)
      );
    } catch {
      return [];
    }
  });
}

export async function fetchBadgeCommitters(badgeId: string): Promise<any[]> {
  return cachedRequest(`modal:badge-committers:${badgeId}`, async () => {
    const response = await getBadgeById(badgeId);
    const committers = response?.data?.badges?.committers || [];
    return Array.isArray(committers) ? committers : [];
  });
}

function extractMembersWhoCanIssueArray(res: any): any[] {
  const d = res?.data;
  const candidates = [
    d?.results,
    d?.data,
    Array.isArray(d) ? d : null,
    d?.results?.data,
    res?.results,
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

export async function fetchMembersWhoCanIssueBadge(
  badgeId: string
): Promise<any[]> {
  return cachedRequest(`modal:badge-issuers:${badgeId}`, async () => {
    const res = await getMembersWhoCanIssueABadge({ badgeId });
    return extractMembersWhoCanIssueArray(res);
  });
}

export function memberDisplayName(m: ModalMemberOption): string {
  if (m.name) return m.name;
  if (m.firstName && m.lastName) {
    return normalizeLabel(`${m.firstName} ${m.lastName}`);
  }
  return m.phone || "";
}

export function findMemberByRecipientLabel(
  label: string,
  members: ModalMemberOption[]
): ModalMemberOption | undefined {
  const n = normalizeLabel(label);
  return members.find((m) => {
    const id = normalizeMemberId(m);
    if (id && (id === label || id === n)) return true;
    if (m.phone && normalizeLabel(String(m.phone)) === n) return true;
    return normalizeLabel(memberDisplayName(m)) === n;
  });
}

export function issuersForBadgeIncludeAllRecipients(
  issuerMembers: any[],
  recipients: string[],
  memberOptions: ModalMemberOption[]
): boolean {
  const issuerSet = new Set<string>();
  for (const m of issuerMembers) {
    const id = normalizeMemberId(m);
    if (id) issuerSet.add(id);
    const p = m?.phone != null ? normalizeLabel(String(m.phone)) : "";
    if (p) issuerSet.add(p);
  }
  return recipients.every((label) => {
    const member = findMemberByRecipientLabel(label, memberOptions);
    if (!member) return false;
    const id = normalizeMemberId(member);
    if (id && issuerSet.has(id)) return true;
    const phone =
      member.phone != null ? normalizeLabel(String(member.phone)) : "";
    return phone ? issuerSet.has(phone) : false;
  });
}

const BADGE_FILTER_CONCURRENCY = 6;

export async function filterBadgesByCommitters(
  badges: ModalBadgeOption[],
  recipients: string[],
  memberOptions: ModalMemberOption[]
): Promise<ModalBadgeOption[]> {
  if (badges.length === 0) return [];

  const checked = await mapWithConcurrency(
    badges,
    BADGE_FILTER_CONCURRENCY,
    async (badge) => {
      try {
        const committers = await fetchBadgeCommitters(badge._id);
        return issuersForBadgeIncludeAllRecipients(
          committers,
          recipients,
          memberOptions
        )
          ? badge
          : null;
      } catch {
        return null;
      }
    }
  );

  return checked.filter(Boolean) as ModalBadgeOption[];
}

export async function filterBadgesByIssuers(
  badges: ModalBadgeOption[],
  recipients: string[],
  memberOptions: ModalMemberOption[]
): Promise<ModalBadgeOption[]> {
  if (badges.length === 0) return [];

  const checked = await mapWithConcurrency(
    badges,
    BADGE_FILTER_CONCURRENCY,
    async (badge) => {
      try {
        const members = await fetchMembersWhoCanIssueBadge(badge._id);
        return issuersForBadgeIncludeAllRecipients(
          members,
          recipients,
          memberOptions
        )
          ? badge
          : null;
      } catch {
        return null;
      }
    }
  );

  return checked.filter(Boolean) as ModalBadgeOption[];
}
